import { computed, inject, Injectable, OnDestroy, signal, WritableSignal, } from '@angular/core';
import { Slide, MenuDisplayTheme, SlideCollection, ISignageScreen, ICustomMenuDisplay, ICustomMenuCategory, CustomMenuDisplayTheme, IScreenCollectionSchedule } from '../models/signage-model';
import {
  applySchedulesToScreen,
  mapSchedulesToFirebase,
  normalizeSignageScreen,
  renameCollectionInSchedules,
  removeCollectionFromSchedules,
  scheduleReferencesCollection,
} from '../signage-screen/signage-schedule.utils';
import { MENU_DISPLAY_DEFAULT_FONT_FAMILY } from '../signage-screen/menu-display/menu-display-google-fonts';
import { UnitService } from './unit.service';
import { AngularFireDatabase } from '@angular/fire/compat/database';

/** RTDB gallery meta + dialog data: signage media is filtered out of the product gallery. */
export type GalleryScope = 'signage' | 'general';

// Signage Media Constants
export const SIGNAGE_MEDIA_CONSTANTS = {
  MAX_VIDEO_BYTES: 50 * 1024 * 1024, // 50 MB
  MAX_IMAGE_BYTES: 10 * 1024 * 1024, // 10 MB (originals; no resizeImage)
  MAX_VIDEO_DURATION_SEC: 60, // 60 seconds
  ACCEPT_VIDEO: 'video/mp4',
  ACCEPT_IMAGE: 'image/jpeg,image/png,image/webp,image/gif',
  STORAGE_PREFIX: 'signage-media',
  GALLERY_SCOPE: 'signage' as GalleryScope,
  SUPPORTED_VIDEO_EXTENSIONS: ['.mp4'] as const,
  SUPPORTED_IMAGE_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.webp', '.gif'] as const,
  RECOMMENDED_DURATION_SEC: 30,
  RECOMMENDED_SIZE_MB: 20,
} as const;

export const SIGNAGE_MEDIA_VALIDATION = {
  VIDEO_MIME_TYPE: 'video/mp4',
  MIN_DURATION_SEC: 1,
  MAX_DURATION_SEC: SIGNAGE_MEDIA_CONSTANTS.MAX_VIDEO_DURATION_SEC,
  MIN_SIZE_BYTES: 1024, // 1 KB minimum
  MAX_SIZE_BYTES: SIGNAGE_MEDIA_CONSTANTS.MAX_VIDEO_BYTES,
  MAX_IMAGE_SIZE_BYTES: SIGNAGE_MEDIA_CONSTANTS.MAX_IMAGE_BYTES,
  ALLOWED_IMAGE_MIME_TYPES: [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
  ] as readonly string[],
} as const;

@Injectable({
  providedIn: 'root',
})
export class SignageScreenService implements OnDestroy {
  private unitService = inject(UnitService);
  private _slides: WritableSignal<Slide[]> = signal<Slide[]>([]);
  readonly slides = this._slides.asReadonly();
  private _slideSet: WritableSignal<SlideCollection[]> = signal<SlideCollection[]>([])
  readonly slideSets = this._slideSet.asReadonly();
  private _screens: WritableSignal<ISignageScreen[]> = signal<any[]>([])
  readonly screens = this._screens.asReadonly();
  private _theme: WritableSignal<MenuDisplayTheme | null> = signal<MenuDisplayTheme | null>(null);
  readonly theme = this._theme.asReadonly();
  private _themeBaseline: WritableSignal<MenuDisplayTheme | null> = signal<MenuDisplayTheme | null>(null);
  readonly themeDirty = computed(() => {
    const current = this._theme();
    const baseline = this._themeBaseline();
    if (!current || !baseline) {
      return false;
    }
    return !this.menuDisplayThemesEqual(current, baseline);
  });
  private _themeLoading: WritableSignal<boolean> = signal(false);
  readonly themeLoading = this._themeLoading.asReadonly();
  private _customMenuDisplays: WritableSignal<ICustomMenuDisplay[]> = signal<ICustomMenuDisplay[]>([]);
  readonly customMenuDisplays = this._customMenuDisplays.asReadonly();

  // Playback failure detection helpers
  readonly hasAnyPlaybackFailed = computed(() => {
    const collections = this.slideSets();
    return collections.some(collection => 
      collection.slides?.some(slide => slide.playbackFailed) ?? false
    );
  });

  collectionHasPlaybackFailed(collection: SlideCollection): boolean {
    return collection.slides?.some(slide => slide.playbackFailed) ?? false;
  }

  private get routeName(): string | undefined {
    return this.unitService.selectedUnit?.routeName;
  }

  constructor(private afDatabase: AngularFireDatabase) {
   }

  private getRouteNameOrThrow(): string {
    if (!this.routeName) {
      throw new Error('No route selected');
    }
    return this.routeName;
  }

  private mapSlides(slidesData: unknown): Slide[] {
    if (!slidesData || typeof slidesData !== 'object') {
      return [];
    }

    return Object.entries(slidesData as Record<string, any>).map(([slideKey, slideValue]) => ({
      key: slideKey,
      ...(slideValue ?? {})
    })) as Slide[];
  }

  private mapSlideCollections(data: unknown): SlideCollection[] {
    if (!data || typeof data !== 'object') {
      return [];
    }

    return Object.entries(data as Record<string, any>).map(([collectionKey, collectionValue]) => ({
      key: collectionKey,
      ...(collectionValue ?? {}),
      slides: this.mapSlides(collectionValue?.slides)
    })) as SlideCollection[];
  }

  sanitizeCustomMenuTheme(theme?: Partial<CustomMenuDisplayTheme> | null): CustomMenuDisplayTheme {
    return {
      bgImageUrl: theme?.bgImageUrl ?? '',
      fontColor: theme?.fontColor ?? '#ffffff',
      logoUrl: theme?.logoUrl ?? '',
      toggleProductImage: theme?.toggleProductImage ?? false,
      fontFamily: theme?.fontFamily?.trim() || MENU_DISPLAY_DEFAULT_FONT_FAMILY,
      defaultLanguage: theme?.defaultLanguage ?? 'en',
    };
  }

  getEmptyCustomMenuTheme(): CustomMenuDisplayTheme {
    return this.sanitizeCustomMenuTheme();
  }

  private productIdsToFirebaseMap(productIds: string[]): Record<string, boolean> {
    return productIds.reduce((acc, id) => {
      acc[id] = true;
      return acc;
    }, {} as Record<string, boolean>);
  }

  private mapCategoriesFromFirebase(categoriesData: unknown): ICustomMenuCategory[] {
    if (!categoriesData || typeof categoriesData !== 'object') {
      return [];
    }
    return Object.entries(categoriesData as Record<string, any>).map(([key, value]) => ({
      key,
      name: value?.name || '',
      products: Object.keys(value?.products || {}),
      textColor: value?.textColor || '#ffffff',
    }));
  }

  private mapCategoriesToFirebase(
    categories: ICustomMenuCategory[]
  ): Record<string, { name: string; products: Record<string, boolean>; textColor: string }> {
    const result: Record<string, { name: string; products: Record<string, boolean>; textColor: string }> = {};
    for (const category of categories) {
      const key = category.key || this.afDatabase.database.ref().push().key!;
      result[key] = {
        name: category.name,
        products: this.productIdsToFirebaseMap(category.products),
        textColor: category.textColor?.trim() || '#ffffff',
      };
    }
    return result;
  }

  private shouldPersistCustomTheme(theme: CustomMenuDisplayTheme): boolean {
    return Boolean(
      theme.bgImageUrl ||
      theme.logoUrl ||
      theme.toggleProductImage ||
      theme.fontColor !== '#ffffff' ||
      (theme.fontFamily && theme.fontFamily !== MENU_DISPLAY_DEFAULT_FONT_FAMILY)
    );
  }

  async getScreens(): Promise<ISignageScreen[]> {
    const routeName = this.getRouteNameOrThrow();
    const snapshot = await this.afDatabase.database.ref(`Clients/${routeName}/signage/screens`).get();

    if (!snapshot.exists()) {
      this._screens.set([]);
      return [];
    }

    const screensData = snapshot.val();
    const screens = Object.entries(screensData as Record<string, any>).map(([screenKey, screenValue]) =>
      normalizeSignageScreen(screenValue ?? {}, screenKey)
    );

    this._screens.set(screens);
    return screens;
  }

  async getSlideSets(): Promise<SlideCollection[]> {
    const routeName = this.getRouteNameOrThrow();
    const snapshot = await this.afDatabase.database
      .ref(`Clients/${routeName}/signage/carousel/slideCollections`)
      .get();

    if (!snapshot.exists()) {
      this._slideSet.set([]);
      return [];
    }

    const slideSets = this.mapSlideCollections(snapshot.val());
    this._slideSet.set(slideSets);
    return slideSets;
  }

  private watchedRouteName: string | null = null;
  private slideCollectionsRef: any = null;
  private slideCollectionsCallback: ((snapshot: any) => void) | null = null;

  stopWatchingSlideSets(): void {
    if (this.slideCollectionsRef && this.slideCollectionsCallback) {
      this.slideCollectionsRef.off('value', this.slideCollectionsCallback);
      this.slideCollectionsRef = null;
      this.slideCollectionsCallback = null;
    }
    this.watchedRouteName = null;
  }

  startWatchingSlideSets(targetRouteName?: string): void {
    const routeName = targetRouteName || this.routeName;
    if (!routeName) return;

    if (this.watchedRouteName === routeName && this.slideCollectionsRef) {
      return;
    }

    // Clean up previous listener before rebinding to a new route
    this.stopWatchingSlideSets();

    this.watchedRouteName = routeName;
    const ref = this.afDatabase.database.ref(`Clients/${routeName}/signage/carousel/slideCollections`);
    const callback = (snapshot: any) => {
      if (snapshot.exists()) {
        const slideSets = this.mapSlideCollections(snapshot.val());
        this._slideSet.set(slideSets);
      } else {
        this._slideSet.set([]);
      }
    };

    ref.on('value', callback);
    this.slideCollectionsRef = ref;
    this.slideCollectionsCallback = callback;
  }

  ngOnDestroy(): void {
    this.stopWatchingSlideSets();
  }

  async getCustomMenuDisplays(): Promise<ICustomMenuDisplay[]> {
    const routeName = this.getRouteNameOrThrow();
    const productsRef = this.afDatabase.database.ref(`Clients/${routeName}/menuItems`);
    const displaysRef = this.afDatabase.database.ref(`Clients/${routeName}/signage/customMenuDisplay`);

    const [productsSnap, displaysSnap] = await Promise.all([productsRef.get(), displaysRef.get()]);
    if (!displaysSnap.exists()) {
      this._customMenuDisplays.set([]);
      return [];
    }

    const productsData = productsSnap.exists() ? (productsSnap.val() as Record<string, any>) : {};
    const displaysData = displaysSnap.val() as Record<string, any>;

    const displays = Object.entries(displaysData).map(([key, value]) => {
      const productIds = Object.keys(value?.products || {});
      const products = productIds.map(pid => ({
        id: pid,
        name: productsData[String(pid)]?.Name || ''
      }));

      return {
        key,
        name: value?.name || '',
        products,
        categories: this.mapCategoriesFromFirebase(value?.categories),
        theme: value?.theme
          ? this.sanitizeCustomMenuTheme(value.theme)
          : undefined
      };
    }) as ICustomMenuDisplay[];

    this._customMenuDisplays.set(displays);
    return displays;
  }

  async createCustomMenuDisplay(
    name: string,
    productKeys: string[],
    theme?: CustomMenuDisplayTheme,
    categories: ICustomMenuCategory[] = []
  ) {
    const routeName = this.getRouteNameOrThrow();
    const productsObj = this.productIdsToFirebaseMap(productKeys);
    const categoriesObj = this.mapCategoriesToFirebase(categories);
    const finalTheme = theme ? this.sanitizeCustomMenuTheme(theme) : undefined;
    const shouldPersistTheme = finalTheme ? this.shouldPersistCustomTheme(finalTheme) : false;

    const ref = this.afDatabase.database.ref(`Clients/${routeName}/signage/customMenuDisplay`).push();
    const customMenuDisplay = {
      key: ref.key ?? '',
      name,
      products: productsObj,
      ...(categories.length > 0 ? { categories: categoriesObj } : {}),
      ...(shouldPersistTheme ? { theme: finalTheme } : {})
    };

    await ref.set(customMenuDisplay);
    const createdMenu: ICustomMenuDisplay = {
      key: customMenuDisplay.key,
      name: customMenuDisplay.name,
      products: productKeys.map(id => ({ id })),
      ...(categories.length > 0 ? { categories } : {}),
      ...(shouldPersistTheme ? { theme: finalTheme } : {})
    };

    const current = this._customMenuDisplays();
    this._customMenuDisplays.set([...current, createdMenu]);

    return createdMenu;
  }

  async editCustomMenuDisplay(menu: ICustomMenuDisplay): Promise<void> {
    if (!menu.key) return;

    const productsObj = this.productIdsToFirebaseMap(menu.products.map(p => p.id));
    const categories = menu.categories ?? [];
    const categoriesObj = this.mapCategoriesToFirebase(categories);

    const routeName = this.getRouteNameOrThrow();
    await this.afDatabase.database
      .ref(`Clients/${routeName}/signage/customMenuDisplay/${menu.key}`)
      .update({
        name: menu.name,
        products: productsObj,
        categories: categories.length > 0 ? categoriesObj : null,
        ...(menu.theme ? { theme: menu.theme } : {})
      });

    this._customMenuDisplays.update(currentMenus => {
      return currentMenus.map(m =>
        m.key === menu.key
          ? { ...m, name: menu.name, products: menu.products, categories, theme: menu.theme ?? m.theme }
          : m
      );
    });
  }

  async deleteCustomMenuDisplay(customMenuKey: string): Promise<void> {
    if (!customMenuKey) return;
    const routeName = this.getRouteNameOrThrow();
    const db = this.afDatabase.database;
    const customMenuRef = db.ref(`Clients/${routeName}/signage/customMenuDisplay/${customMenuKey}`);
    const collectionsRef = db.ref(`Clients/${routeName}/signage/carousel/slideCollections`);

    const [menuSnap, collectionsSnap] = await Promise.all([customMenuRef.get(), collectionsRef.get()]);
    if (!menuSnap.exists()) {
      return;
    }

    if (collectionsSnap.exists()) {
      const collections = collectionsSnap.val() as Record<string, any>;
      const updates: Record<string, any> = {};

      Object.entries(collections).forEach(([collectionKey, collectionValue]) => {
        const slides = collectionValue?.slides || {};
        Object.entries(slides).forEach(([slideKey, slideValue]) => {
          const slideEntry = slideValue as any;
          if (
            slideEntry?.type === 'customMenu' &&
            String(slideEntry?.customMenuDisplayKey) === String(customMenuKey)
          ) {
            updates[`Clients/${routeName}/signage/carousel/slideCollections/${collectionKey}/slides/${slideKey}/customMenuDisplayKey`] = null;
            updates[`Clients/${routeName}/signage/carousel/slideCollections/${collectionKey}/slides/${slideKey}/customMenuBgUrl`] = null;
            updates[`Clients/${routeName}/signage/carousel/slideCollections/${collectionKey}/slides/${slideKey}/isPublished`] = false;
          }
        });
      });

      if (Object.keys(updates).length > 0) {
        await db.ref().update(updates);
      }
    }

    await customMenuRef.remove();

    this._customMenuDisplays.update(currentMenus =>
      currentMenus.filter(menu => menu.key !== customMenuKey)
    );
  }

  async createSlideCollection(name: string) {
    const routeName = this.getRouteNameOrThrow();
    const ref = this.afDatabase.database.ref(`Clients/${routeName}/signage/carousel/slideCollections`).push();

    const newCollection: SlideCollection = {
      key: ref.key ?? '',
      name,
      slides: [],
      createdAt: Date.now(),
    };

    await ref.set({
      key: newCollection.key,
      name: newCollection.name,
      slides: {},
      createdAt: newCollection.createdAt
    });

    this._slideSet.update(list =>
      list.some(c => c.key === newCollection.key) ? list : [...list, newCollection]
    );
    return newCollection;
  }

  async renameSlideCollection(collectionKey: string, newName: string) {
    const trimmed = newName?.trim() ?? '';
    if (!collectionKey || trimmed.length < 3) {
      return;
    }

    const routeName = this.getRouteNameOrThrow();
    await this.afDatabase.database
      .ref(`Clients/${routeName}/signage/carousel/slideCollections/${collectionKey}`)
      .update({ name: trimmed });

    await this.patchScreensForCollection(collectionKey, schedules =>
      renameCollectionInSchedules(schedules, collectionKey, trimmed),
    );

    this._slideSet.update(list =>
      list.map(c => (c.key === collectionKey ? { ...c, name: trimmed } : c))
    );
  }

  private async patchScreensForCollection(
    collectionKey: string,
    mutateSchedules: (schedules: IScreenCollectionSchedule[]) => IScreenCollectionSchedule[],
  ): Promise<void> {
    if (!this._screens().length) {
      await this.getScreens();
    }

    const routeName = this.getRouteNameOrThrow();
    const updates: Record<string, unknown> = {};
    let changed = false;

    const nextScreens = this._screens().map(screen => {
      if (!scheduleReferencesCollection(screen.collectionSchedules, collectionKey)) {
        return screen;
      }
      changed = true;
      const next = applySchedulesToScreen(screen, mutateSchedules(screen.collectionSchedules));
      updates[`Clients/${routeName}/signage/screens/${screen.key}/collectionSchedules`] =
        mapSchedulesToFirebase(next.collectionSchedules);
      if (!next.isPublished && screen.isPublished) {
        updates[`Clients/${routeName}/signage/screens/${screen.key}/isPublished`] = false;
      }
      return next;
    });

    if (!changed) {
      return;
    }

    await this.afDatabase.database.ref().update(updates);
    this._screens.set(nextScreens);
  }

  private screenToFirebasePayload(screen: ISignageScreen): Record<string, unknown> {
    return {
      name: screen.name,
      isPublished: screen.isPublished ?? false,
      collectionSchedules: mapSchedulesToFirebase(screen.collectionSchedules ?? []),
    };
  }

  async createScreen(screen: ISignageScreen) {
    const routeName = this.getRouteNameOrThrow();
    const ref = this.afDatabase.database.ref(`Clients/${routeName}/signage/screens`).push();
    const screenData: ISignageScreen = {
      ...screen,
      key: ref.key ?? ''
    };

    await ref.set({
      ...this.screenToFirebasePayload(screenData),
      key: screenData.key,
      createdAt: Date.now(),
    });

    this._screens.update(list => [...list, screenData]);
  }

  async createSlide(slide: Slide, slideCollectionKey: string) {
    const routeName = this.getRouteNameOrThrow();
    const slidesRef = this.afDatabase.database
      .ref(`Clients/${routeName}/signage/carousel/slideCollections/${slideCollectionKey}/slides`);
    const ref = slidesRef.push();

    const slideWithId: Slide = {
      ...slide,
      key: ref.key ?? '',
      isPublished: slide.isPublished ?? false,
      // Clear playback failure fields when creating new slide
      playbackFailed: null,
      playbackFailedAt: null,
      playbackFailedReason: null
    };

    await ref.set(slideWithId);

    return slideWithId;
  }

  async deleteScreen(screenId: string) {
    try {
      if (!screenId) {
        return;
      }
      const routeName = this.getRouteNameOrThrow();
      await this.afDatabase.database.ref(`Clients/${routeName}/signage/screens/${screenId}`).remove();

      this._screens.update(screens => screens.filter(screen => screen.key !== screenId));

    } catch (error) {
      console.error('Failed to delete screen', error);
    }
  }

  async deleteSlideCollection(slideCollectionKey: string) {
    if (!slideCollectionKey) return;

    const previousCollections = this._slideSet();
    if (!previousCollections.some(c => c.key === slideCollectionKey)) {
      return;
    }

    this._slideSet.update(list => list.filter(c => c.key !== slideCollectionKey));

    const routeName = this.getRouteNameOrThrow();
    const collectionRef = this.afDatabase.database.ref(
      `Clients/${routeName}/signage/carousel/slideCollections/${slideCollectionKey}`,
    );

    try {
      await Promise.all([
        this.patchScreensForCollection(slideCollectionKey, schedules =>
          removeCollectionFromSchedules(schedules, slideCollectionKey),
        ),
        collectionRef.remove(),
      ]);
    } catch (error) {
      this._slideSet.set(previousCollections);
      console.error('Failed to delete slide collection', error);
      throw error;
    }
  }

  async deleteSlide(slideCollectionKey: string, slideId: string) {
    const routeName = this.getRouteNameOrThrow();
    await this.afDatabase.database
      .ref(`Clients/${routeName}/signage/carousel/slideCollections/${slideCollectionKey}/slides/${slideId}`)
      .remove();

    this._slideSet.update(list =>
      list.map(collection =>
        collection.key === slideCollectionKey
          ? {
            ...collection,
            slides: collection.slides?.filter(slide => slide.key !== slideId)
          }
          : collection
      )
    )

  }
  async toggleSlideStatus(slideKey: string, currentStatus: boolean, slideCollectionKey: string) {
    const newStatus = !currentStatus;
    const routeName = this.getRouteNameOrThrow();

    await this.afDatabase.database
      .ref(`Clients/${routeName}/signage/carousel/slideCollections/${slideCollectionKey}/slides/${slideKey}`)
      .update({ isPublished: newStatus });

    this._slideSet.update(list =>
      list.map(collection =>
        collection.key === slideCollectionKey
          ? {
            ...collection,
            slides: collection.slides?.map(slide =>
              slide.key === slideKey ? { ...slide, isPublished: newStatus } : slide
            ),
          }
          : collection
      )
    );

    return newStatus;
  }


  async editSlide(updatedSlide: Slide, slideCollectionKey: string) {
    const slideToSend = {
      ...updatedSlide,
      imageUrl: updatedSlide.imageUrl ?? null,
      name: updatedSlide.name,
      delaySeconds: updatedSlide.delaySeconds ?? 5,
      customMenuDisplayKey: updatedSlide.customMenuDisplayKey ?? null,
      customMenuBgUrl: updatedSlide.customMenuBgUrl ?? null,
      videoUrl: updatedSlide.videoUrl ?? null,
      durationSeconds: updatedSlide.durationSeconds ?? null,
    };

    try {
      const routeName = this.getRouteNameOrThrow();
      await this.afDatabase.database
        .ref(`Clients/${routeName}/signage/carousel/slideCollections/${slideCollectionKey}/slides/${updatedSlide.key}`)
        .update({
          name: slideToSend.name || '',
          delaySeconds: slideToSend.delaySeconds ?? 5,
          type: slideToSend.type || 'activeMenu',
          imageUrl: slideToSend.imageUrl ?? null,
          customMenuDisplayKey: slideToSend.customMenuDisplayKey ?? null,
          customMenuBgUrl: slideToSend.customMenuBgUrl ?? null,
          videoUrl: slideToSend.videoUrl ?? null,
          durationSeconds: slideToSend.durationSeconds ?? null,
          // Clear playback failure fields when admin saves/edits slide
          playbackFailed: null,
          playbackFailedAt: null,
          playbackFailedReason: null,
        });

      this._slideSet.update(collections =>
        collections.map(collection =>
          collection.key === slideCollectionKey
            ? {
              ...collection,
              slides: collection.slides?.map(slide =>
                slide.key === updatedSlide.key
                  ? { ...slide, ...slideToSend }
                  : slide
              ),
            }
            : collection
        )
      );
    } catch (error) {
      console.error('Failed to edit slide:', error);
      throw error;
    }
  }

  async editScreen(screen: ISignageScreen) {
    try {
      if (!screen?.key) {
        return;
      }

      const screenKey = String(screen.key);
      const routeName = this.getRouteNameOrThrow();
      await this.afDatabase.database
        .ref(`Clients/${routeName}/signage/screens/${screenKey}`)
        .update({
          key: screenKey,
          ...this.screenToFirebasePayload(screen),
        });

      this._screens.update(screens =>
        screens.map(s => String(s.key) === screenKey ? { ...screen, key: screenKey } : s)
      );

    } catch (error) {
      console.error('Failed to update screen', error);
      throw error;
    }
  }


  async getTheme(): Promise<MenuDisplayTheme> {
    this._themeLoading.set(true);
    try {
      const routeName = this.getRouteNameOrThrow();
      const snapshot = await this.afDatabase.database.ref(`Clients/${routeName}/theme/menuDisplay`).get();
      const raw = (snapshot.exists() ? snapshot.val() : {}) as Partial<MenuDisplayTheme>;
      const response: MenuDisplayTheme = {
        fontColor: raw.fontColor ?? '#ffffff',
        bgImageUrl: raw.bgImageUrl ?? '',
        logoUrl: raw.logoUrl ?? '',
        toggleProductImage: raw.toggleProductImage ?? false,
        fontFamily: raw.fontFamily?.trim() || MENU_DISPLAY_DEFAULT_FONT_FAMILY,
        defaultLanguage: raw.defaultLanguage ?? 'en',
      };
      this._theme.set(response);
      this._themeBaseline.set(this.cloneMenuDisplayTheme(response));
      return response;
    } finally {
      this._themeLoading.set(false);
    }
  }

  async saveTheme() {
    const current = this._theme();
    if (!current) {
      return;
    }
    const routeName = this.getRouteNameOrThrow();
    await this.afDatabase.database
      .ref(`Clients/${routeName}/theme/menuDisplay`)
      .update(current as any);
    this._themeBaseline.set(this.cloneMenuDisplayTheme(current));
  }

  private cloneMenuDisplayTheme(theme: MenuDisplayTheme): MenuDisplayTheme {
    return { ...theme };
  }

  private menuDisplayThemesEqual(a: MenuDisplayTheme, b: MenuDisplayTheme): boolean {
    return (
      a.fontColor === b.fontColor &&
      a.bgImageUrl === b.bgImageUrl &&
      a.logoUrl === b.logoUrl &&
      a.toggleProductImage === b.toggleProductImage &&
      a.fontFamily === b.fontFamily &&
      a.defaultLanguage === b.defaultLanguage
    );
  }

  updateLogo(logoUrl: string) {
    const current = this._theme();
    if (!current || current.logoUrl === logoUrl) {
      return;
    }
    this._theme.update(theme => ({ ...theme!, logoUrl }));
  }

  updateBgImage(bgImageUrl: string) {
    const current = this._theme();
    if (!current || current.bgImageUrl === bgImageUrl) {
      return;
    }
    this._theme.update(theme => ({ ...theme!, bgImageUrl }));
  }

  updateFontColor(fontColor: string) {
    const current = this._theme();
    if (!current || current.fontColor === fontColor) {
      return;
    }
    this._theme.update(theme => ({ ...theme!, fontColor }));
  }

  updateFontFamily(fontFamily: string) {
    const next = fontFamily?.trim() || MENU_DISPLAY_DEFAULT_FONT_FAMILY;
    const current = this._theme();
    if (!current || current.fontFamily === next) {
      return;
    }
    this._theme.update(theme => ({ ...theme!, fontFamily: next }));
  }

  updateToggleProductImage(toggle: boolean) {
    const current = this._theme();
    if (!current || current.toggleProductImage === toggle) {
      return;
    }
    this._theme.update(theme => ({ ...theme!, toggleProductImage: toggle }));
  }

  updateDefaultLanguage(defaultLanguage: string) {
    const next = defaultLanguage?.trim() || 'en';
    const current = this._theme();
    if (!current || current.defaultLanguage === next) {
      return;
    }
    this._theme.update(theme => ({ ...theme!, defaultLanguage: next }));
  }

  editMenuDisplayImages(type: 'logo' | 'bg', result: any | string) {
    const current = this._theme();
    if (!current) {
      return;
    }

    if (type === 'logo') {
      const logoUrl = typeof result === 'string' ? result : result.imageUrl || '';
      if (current.logoUrl === logoUrl) {
        return;
      }
      this._theme.update(theme => ({ ...theme!, logoUrl }));
    } else if (type === 'bg') {
      const bgImageUrl = typeof result === 'string' ? result : result.imageUrl || '';
      if (current.bgImageUrl === bgImageUrl) {
        return;
      }
      this._theme.update(theme => ({ ...theme!, bgImageUrl }));
    }
  }


}


